/* ============================================================================
   Artisticases — collection page behaviour (sections/ac-collection-page.liquid)

   Filtering, sorting and paging go through Shopify: the section is fetched
   with ?section_id=<id>&<params> and every [data-ac-swap] region is replaced,
   then the URL is pushed so state is shareable and Back works.

   State lives in the URL (filter.* pairs, sort_by, page). Both forms — the
   desktop dropdowns and the phone drawer — are re-checked from it after each
   render, which also makes the fixture facets (params Shopify ignores until
   Search & Discovery activates them) behave consistently.

   Desktop dropdown changes apply immediately (debounced). The drawer holds a
   draft until SHOW RESULTS; closing it discards the draft.

   Search: the same section renders /search (templates/search.json). The q,
   type and options[prefix] params are not filter.* params, so buildParams
   keeps them across filter, sort and page changes, and the section fetch
   carries them too.

   Swatches swap the card image and links to the variant; nothing navigates.
   Hearts are the theme's wishlist hook; after a render the store-lists engine
   is asked to re-mark cards already on the list.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-ac-col]');
  if (!root) return;

  var sectionId = root.getAttribute('data-section-id');
  var baseUrl = root.getAttribute('data-collection-url') || window.location.pathname;
  var results = root.querySelector('[data-ac-results]');
  var errorBox = root.querySelector('[data-ac-error]');
  var retryBtn = root.querySelector('[data-ac-retry]');
  var sortSelect = root.querySelector('[data-ac-sort]');
  var chips = root.querySelector('[data-ac-chips]');
  var desktopForm = root.querySelector('[data-ac-form="desktop"]');
  var drawer = root.querySelector('[data-ac-drawer]');
  var drawerForm = root.querySelector('[data-ac-form="mobile"]');
  var openBtn = root.querySelector('[data-ac-open-drawer]');
  var bar = root.querySelector('[data-ac-bar]');
  var header = document.getElementById('header');
  var FACETS = ['type', 'design', 'color', 'personalization'];
  var DESKTOP_MIN = 1025;

  var cache = {};
  var activeController = null;
  var lastRequest = null;
  var lastFocus = null;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }
  function rafThrottle(fn) {
    var pending = false;
    return function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () { pending = false; fn(); });
    };
  }
  function isStateParam(key) {
    return key.indexOf('filter.') === 0 || key === 'sort_by' || key === 'page';
  }
  /* Shopify's search sends the bracket key `options[prefix]`; a decoded
     URLSearchParams round-trip turns it into a Ruby-hash string on the next
     push, so its raw spelling is kept from the initial URL. */
  function rawParam(name) {
    var m = window.location.search.match(new RegExp('[?&](' + name.replace(/[[\]]/g, '\\$&') + '=[^&]*)'));
    return m ? m[1] : null;
  }
  function pairKey(name, value) { return JSON.stringify([name, value]); }
  function currentParams() { return new URLSearchParams(window.location.search); }
  function pairsFromParams(params) {
    var out = [];
    params.forEach(function (v, k) { if (k.indexOf('filter.') === 0) out.push([k, v]); });
    return out;
  }
  function pairsFromForm(form) {
    var out = [];
    if (!form) return out;
    form.querySelectorAll('input[type="checkbox"]:checked').forEach(function (i) { out.push([i.name, i.value]); });
    return out;
  }
  /* Keeps params that are not ours (preview_theme_id, utm_*, …). */
  function buildParams(pairs, sort, page) {
    var p = new URLSearchParams();
    currentParams().forEach(function (v, k) { if (!isStateParam(k) && k !== 'options[prefix]' && k.indexOf('options') !== 0) p.append(k, v); });
    pairs.forEach(function (pr) { p.append(pr[0], pr[1]); });
    if (sort && sortSelect && sort !== sortSelect.getAttribute('data-default')) p.set('sort_by', sort);
    if (page && page > 1) p.set('page', String(page));
    return p;
  }
  function currentSort() { return sortSelect ? sortSelect.value : ''; }

  /* --------------------------------------------------------- fetch/render */

  function setBusy(on) {
    if (results) results.setAttribute('aria-busy', on ? 'true' : 'false');
  }
  function hideError() { if (errorBox) errorBox.hidden = true; }
  function showError() { if (errorBox) errorBox.hidden = false; }

  function load(params, opts) {
    opts = opts || {};
    var qs = params.toString();
    var prefix = rawParam('options[prefix]') || rawParam('options%5Bprefix%5D');
    if (prefix) qs = qs ? qs + '&' + prefix : prefix;
    var pageUrl = baseUrl + (qs ? '?' + qs : '');
    var fetchParams = new URLSearchParams(qs);
    fetchParams.set('section_id', sectionId);
    var fetchUrl = baseUrl + '?' + fetchParams.toString();

    lastRequest = { params: params, opts: opts };
    if (opts.push !== false) window.history.pushState({ acCol: true }, '', pageUrl);
    setBusy(true);
    hideError();
    if (activeController) activeController.abort();
    var ctrl = activeController = (typeof window.AbortController === 'function') ? new window.AbortController() : null;

    function done(html) {
      if (ctrl && ctrl !== activeController) return;
      activeController = null;
      render(html);
      setBusy(false);
      afterRender(opts);
    }

    if (cache[fetchUrl]) { done(cache[fetchUrl]); return; }

    window.fetch(fetchUrl, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) { cache[fetchUrl] = html; done(html); })
      .catch(function (e) {
        if (e && e.name === 'AbortError') return;
        activeController = null;
        setBusy(false);
        showError();
      });
  }

  function render(html) {
    var doc = new window.DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('[data-ac-swap]').forEach(function (fresh) {
      var key = fresh.getAttribute('data-ac-swap');
      var cur = root.querySelector('[data-ac-swap="' + key + '"]');
      if (cur) cur.innerHTML = fresh.innerHTML;
    });
    var freshSort = doc.querySelector('[data-ac-sort]');
    if (freshSort && sortSelect) sortSelect.value = freshSort.value;
  }

  function afterRender(opts) {
    syncFromUrl();
    updateMoreButtons();
    markWishlist(results);
    if (window.theme && window.theme.MultiCurrency && typeof window.theme.MultiCurrency.update === 'function') {
      try { window.theme.MultiCurrency.update(); } catch (e) { /* theme optional */ }
    }
    if (opts && opts.scroll) scrollToResults();
    /* swatch tooltips are the theme's Tippy; rebind for the new cards */
    if (window.theme && window.theme.Tooltip && typeof window.theme.Tooltip.reinit === 'function') {
      try { window.theme.Tooltip.reinit(); } catch (e) { /* theme optional */ }
    }
    root.dispatchEvent(new CustomEvent('ac:collection:rendered', { bubbles: true }));
  }

  function markWishlist(scope) {
    try {
      var t = window.theme;
      var engine = t && t.StoreLists && t.StoreLists.lists && t.StoreLists.lists.wishlist;
      if (engine && typeof engine.checkProductStatus === 'function') {
        engine.checkProductStatus((scope || root).querySelectorAll('[data-js-product]'));
      }
    } catch (e) { /* theme optional */ }
  }

  function scrollToResults() {
    if (!results) return;
    var barH = bar ? bar.getBoundingClientRect().height : 0;
    var offset = parseFloat(getComputedStyle(root).getPropertyValue('--ac-col-sticky-top')) || 0;
    /* Aim at the first case photo, which can sit above the card box (2048px zoom). */
    var firstImg = results.querySelector('img');
    var targetTop = results.getBoundingClientRect().top;
    if (firstImg) targetTop = Math.min(targetTop, firstImg.getBoundingClientRect().top);
    var top = targetTop + window.pageYOffset - barH - offset - 12;
    /* Scrolling UP (pagination sits at the bottom) brings the slim sticky header back in,
       so leave room for it even when it is hidden at click time (2026-11-03). */
    if (top < window.pageYOffset && offset === 0) top -= stickyHeaderH || measureStickyHeader();
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /* --------------------------------------------------------------- state */

  function syncChecks(form, pairs) {
    if (!form) return;
    var set = {};
    pairs.forEach(function (p) { set[pairKey(p[0], p[1])] = true; });
    form.querySelectorAll('input[type="checkbox"]').forEach(function (i) {
      i.checked = !!set[pairKey(i.name, i.value)];
    });
  }
  function syncFromUrl() {
    var pairs = pairsFromParams(currentParams());
    syncChecks(desktopForm, pairs);
    if (drawer && drawer.hidden) syncChecks(drawerForm, pairs); /* an open drawer keeps its draft */
    updateBadges();
    updateChips();
  }

  function updateBadges() {
    var total = 0;
    FACETS.forEach(function (key) {
      var dd = desktopForm ? desktopForm.querySelector('[data-ac-dd="' + key + '"]') : null;
      if (!dd) return;
      var n = dd.querySelectorAll('input[type="checkbox"]:checked').length;
      total += n;
      root.querySelectorAll('[data-ac-badge="' + key + '"]').forEach(function (b) {
        b.textContent = String(n);
        b.hidden = n === 0;
      });
      if (dd) {
        var btn = dd.querySelector('[data-ac-dd-btn]');
        if (btn) btn.classList.toggle('is-active', n > 0);
      }
    });
    var tot = root.querySelector('[data-ac-active-total]');
    if (tot) { tot.textContent = String(total); tot.hidden = total === 0; }
  }

  function updateChips() {
    if (!chips || !desktopForm) return;
    chips.innerHTML = '';
    var checked = desktopForm.querySelectorAll('input[type="checkbox"]:checked');
    if (!checked.length) { chips.hidden = true; return; }
    checked.forEach(function (input) {
      var opt = input.closest('[data-ac-opt]');
      var labelEl = opt ? opt.querySelector('.ac-col__opt-label') : null;
      var label = labelEl ? labelEl.textContent.trim() : input.value;
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'ac-col__chip';
      chip.setAttribute('data-name', input.name);
      chip.setAttribute('data-value', input.value);
      chip.setAttribute('aria-label', 'Remove filter ' + label);
      chip.innerHTML = '<span></span><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      chip.querySelector('span').textContent = label;
      chips.appendChild(chip);
    });
    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'ac-col__chips-clear';
    clear.setAttribute('data-ac-clear-all', '');
    clear.textContent = chips.getAttribute('data-clear-label') || 'Clear all';
    chips.appendChild(clear);
    chips.hidden = false;
  }

  function applyPairs(pairs, opts) {
    load(buildParams(pairs, currentSort(), null), opts);
  }

  /* ------------------------------------------------------------ dropdowns */

  function closeDropdowns(except) {
    root.querySelectorAll('[data-ac-dd].is-open').forEach(function (dd) {
      if (dd === except) return;
      dd.classList.remove('is-open');
      var btn = dd.querySelector('[data-ac-dd-btn]');
      var panel = dd.querySelector('.ac-col__dd-panel');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });
  }
  function openDropdown(dd) {
    closeDropdowns(dd);
    dd.classList.add('is-open');
    var btn = dd.querySelector('[data-ac-dd-btn]');
    var panel = dd.querySelector('.ac-col__dd-panel');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    if (panel) {
      panel.hidden = false;
      var first = panel.querySelector('input[type="checkbox"]');
      if (first) first.focus({ preventScroll: true });
    }
  }
  root.querySelectorAll('[data-ac-dd]').forEach(function (dd) {
    var btn = dd.querySelector('[data-ac-dd-btn]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      if (dd.classList.contains('is-open')) closeDropdowns(); else openDropdown(dd);
    });
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('[data-ac-dd]')) closeDropdowns();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = root.querySelector('[data-ac-dd].is-open');
    if (open) {
      closeDropdowns();
      var btn = open.querySelector('[data-ac-dd-btn]');
      if (btn) btn.focus();
    }
    if (drawer && !drawer.hidden) closeDrawer();
  });

  if (desktopForm) {
    var applyDesktop = debounce(function () { applyPairs(pairsFromForm(desktopForm)); }, 220);
    desktopForm.addEventListener('change', function (e) {
      if (e.target && e.target.type === 'checkbox') applyDesktop();
    });
    desktopForm.addEventListener('submit', function (e) { e.preventDefault(); applyPairs(pairsFromForm(desktopForm)); });
  }

  /* ----------------------------------------------------------------- sort */

  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      load(buildParams(pairsFromParams(currentParams()), sortSelect.value, null));
    });
  }

  /* ---------------------------------------------------- chips / show more */

  root.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest) return;

    var chip = t.closest('.ac-col__chip');
    if (chip) {
      var name = chip.getAttribute('data-name'), value = chip.getAttribute('data-value');
      var pairs = pairsFromParams(currentParams()).filter(function (p) { return !(p[0] === name && p[1] === value); });
      applyPairs(pairs);
      return;
    }
    var clearAll = t.closest('[data-ac-clear-all]');
    if (clearAll) {
      e.preventDefault();
      applyPairs([]);
      return;
    }
    var more = t.closest('[data-ac-more]');
    if (more) {
      var values = more.parentNode.querySelector('[data-ac-values]');
      if (!values) return;
      var expanded = values.classList.toggle('is-expanded');
      more.textContent = expanded ? (more.getAttribute('data-less') || 'Show less') : (more.getAttribute('data-more') || 'Show more');
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      return;
    }
    var page = t.closest('[data-ac-page]');
    if (page) {
      e.preventDefault();
      load(buildParams(pairsFromParams(currentParams()), currentSort(), parseInt(page.getAttribute('data-ac-page'), 10)), { scroll: true });
      return;
    }
    var swatch = t.closest('[data-ac-swatch]');
    if (swatch) {
      e.preventDefault();
      selectSwatch(swatch);
    }
  });

  function updateMoreButtons() {
    root.querySelectorAll('[data-ac-more]').forEach(function (more) {
      var values = more.parentNode.querySelector('[data-ac-values]');
      var hasExtra = values && values.querySelector('.ac-col__opt--extra');
      more.hidden = !hasExtra;
      if (!hasExtra && values) values.classList.remove('is-expanded');
      if (!more.hidden && !values.classList.contains('is-expanded')) {
        more.textContent = more.getAttribute('data-more') || 'Show more';
        more.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* -------------------------------------------------------------- swatches */

  function selectSwatch(sw) {
    var card = sw.closest('[data-ac-card]');
    if (!card || sw.classList.contains('is-active')) return;
    card.querySelectorAll('[data-ac-swatch]').forEach(function (s) {
      var on = s === sw;
      s.classList.toggle('is-active', on);
      s.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var img = card.querySelector('[data-ac-card-img]');
    var src = sw.getAttribute('data-image');
    if (img && src && img.getAttribute('src') !== src) {
      img.setAttribute('src', src);
      img.setAttribute('srcset', sw.getAttribute('data-srcset') || '');
    }
    var url = sw.getAttribute('data-variant-url');
    if (url) card.querySelectorAll('[data-ac-card-link]').forEach(function (a) { a.setAttribute('href', url); });
    var vid = sw.getAttribute('data-variant-id');
    if (vid) {
      card.setAttribute('data-product-variant-id', vid);
      var heart = card.querySelector('[data-ac-heart]');
      if (heart) heart.setAttribute('data-product-variant-id', vid);
      markWishlist(card.parentNode);
    }
  }
  if (results && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    results.addEventListener('mouseover', function (e) {
      var sw = e.target.closest && e.target.closest('[data-ac-swatch]');
      if (sw) selectSwatch(sw);
    });
  }

  /* ---------------------------------------------------------------- drawer */

  function focusables(el) {
    return Array.prototype.filter.call(
      el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary, [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null; }
    );
  }
  function openDrawer() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    syncChecks(drawerForm, pairsFromParams(currentParams()));
    drawer.hidden = false;
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ac-col-lock');
    document.body.classList.add('ac-col-lock');
    var close = drawer.querySelector('.ac-col__drawer-close');
    (close || drawerForm).focus();
  }
  function closeDrawer() {
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('ac-col-lock');
    document.body.classList.remove('ac-col-lock');
    syncChecks(drawerForm, pairsFromParams(currentParams())); /* discard the draft */
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }
  if (openBtn) openBtn.addEventListener('click', openDrawer);
  if (drawer) {
    drawer.querySelectorAll('[data-ac-drawer-close]').forEach(function (el) { el.addEventListener('click', closeDrawer); });
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var list = focusables(drawerForm);
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === drawerForm)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }
  if (drawerForm) {
    drawerForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var pairs = pairsFromForm(drawerForm);
      closeDrawer();
      applyPairs(pairs, { scroll: true });
    });
    var clearBtn = drawerForm.querySelector('[data-ac-drawer-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        drawerForm.querySelectorAll('input[type="checkbox"]:checked').forEach(function (i) { i.checked = false; });
      });
    }
  }
  window.addEventListener('resize', debounce(function () {
    if (window.innerWidth >= DESKTOP_MIN) closeDrawer();
  }, 150));

  /* -------------------------------------------------------- retry / back */

  if (retryBtn) {
    retryBtn.addEventListener('click', function () {
      if (!lastRequest) return;
      var o = {}; for (var k in lastRequest.opts) o[k] = lastRequest.opts[k];
      o.push = false;
      load(lastRequest.params, o);
    });
  }
  window.addEventListener('popstate', function () {
    closeDropdowns();
    load(currentParams(), { push: false });
  });

  /* ---------------------------------------------------- sticky bar offset */

  /* The theme's slim header bar (module.sticky-header.js) fixes itself on
     scroll-up and unfixes on scroll-down; the toolbar sits under it when it
     is there and at the top of the viewport when it is not. */
  var stickyHeaderH = 0;
  /* Before the header has stuck once: the desktop menu row is what sticks on wide
     screens (0 high on phones), the whole mobile header on phones. */
  function measureStickyHeader() {
    if (!header) return 0;
    var desk = header.querySelector('[data-js-desktop-sticky]');
    var mob = header.querySelector('[data-js-mobile-sticky]');
    var h = desk ? desk.getBoundingClientRect().height : 0;
    if (!h && mob) h = mob.getBoundingClientRect().height;
    return Math.round(h);
  }
  function updateStickyOffset() {
    var off = 0;
    if (header && header.classList.contains('header--sticky')) {
      var stuck = header.querySelector('.header__content--sticky');
      if (stuck) {
        var r = stuck.getBoundingClientRect();
        if (r.height > 0 && r.bottom > 0 && r.top <= 1) off = Math.round(r.bottom);
        if (r.height > 0) stickyHeaderH = Math.round(r.height);
      }
    }
    root.style.setProperty('--ac-col-sticky-top', off + 'px');
  }
  var onScroll = rafThrottle(updateStickyOffset);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  updateStickyOffset();
  setTimeout(updateStickyOffset, 600);

  /* ------------------------------------------------------------- initial */

  syncFromUrl();
  updateMoreButtons();
  markWishlist(root);
  setTimeout(function () { markWishlist(root); }, 1500);
})();
